// src/utils/gameHelpers.ts
import { Position, Piece, Board, PieceColor } from "../types/Board.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";

// Piece type to FEN character mapping for Expanded Chess
const PIECE_TYPE_TO_FEN: Record<string, string> = {
  empress: "e",
  chancellor: "c",
  emperor: "E",
  queen: "q",
  duke: "d",
  king: "k",
  fortress: "f",
  citadel: "C",
  cathedral: "T",
  castle: "r",
  rook: "r",
  armory: "A",
  abbey: "Y",
  pope: "P",
  archbishop: "a",
  cardinal: "D",
  bishop: "b",
  priest: "I",
  nun: "U",
  marshall: "m",
  captain: "t",
  sergeant: "s",
  guard: "g",
  archer: "R",
  knight: "n",
  catapult: "L",
  ram: "M",
  mace: "Z",
  lance: "l",
  hatchet: "h",
  sword: "w",
  angel: "G",
  saint: "S",
  wizard: "W",
  squire: "Q",
  knave: "V",
  pawn: "p",
};

/**
 * Creates a unique key for a board square
 */
export const getSquareKey = (row: number, col: number): string => `${row}-${col}`;

/**
 * Determines if a square should be light colored based on chess board pattern
 */
export const isLightSquare = (row: number, col: number): boolean => {
  return (row + col) % 2 === 0;
};

/**
 * Converts column number to file label (a, b, c... z, aa, ab, etc.)
 */
export const getFileLabel = (col: number): string => {
  if (col < 26) {
    return String.fromCharCode(97 + col);
  } else {
    const firstLetter = String.fromCharCode(97 + Math.floor(col / 26) - 1);
    const secondLetter = String.fromCharCode(97 + (col % 26));
    return firstLetter + secondLetter;
  }
};

/**
 * Converts row number to rank label (8, 7, 6... 1)
 */
export const getRankLabel = (row: number, boardRows: number): string => {
  return String(boardRows - row);
};

/**
 * Gets a piece at a specific position on the board
 */
export const getPieceAt = (
  row: number,
  col: number,
  pieces: Map<string, Piece>
): Piece | undefined => {
  return pieces.get(getSquareKey(row, col));
};

/**
 * Creates a Board object from the pieces map
 */
export const createBoardObject = (
  pieces: Map<string, Piece>,
  boardRows: number,
  boardCols: number,
  currentGameId: string
): Board => {
  const squares: (Piece | null)[][] = Array(boardRows)
    .fill(null)
    .map(() => Array(boardCols).fill(null));

  // Create pieces array for AI engine compatibility
  const piecesArray: Piece[] = [];

  pieces.forEach((piece, key) => {
    const [row, col] = key.split("-").map(Number);
    squares[row][col] = piece;
    piecesArray.push(piece);
  });

  return {
    dimensions: { rows: boardRows, cols: boardCols },
    squares,
    pieces: piecesArray,  // ADD THIS - array of pieces for AI engine
    piecesMap: pieces,
    metadata: { variantId: currentGameId, createdAt: new Date() },
  };
};

/**
 * Calculates piece counts and values for both colors
 */
export const calculatePieceStats = (
  pieces: Map<string, Piece>,
  boardRows: number
) => {
  let whiteCount = 0;
  let blackCount = 0;
  let whiteValue = 0;
  let blackValue = 0;
  const boardSizeStr = `${boardRows}×${boardRows}`;

  pieces.forEach((piece) => {
    if (piece.color === "white") {
      whiteCount++;
    } else {
      blackCount++;
    }

    const definition = PIECE_DEFINITIONS[piece.type.toLowerCase()];
    if (definition) {
      const abbreviation = definition.abbreviation.toUpperCase();
      try {
        const pieceValuesModule = require("../data/pieceValues.ts");
        const pieceValues = pieceValuesModule.PIECE_VALUES[abbreviation];
        if (pieceValues) {
          const valueEntry = pieceValues.find(
            (v: any) => v.boardSize === boardSizeStr
          );
          if (valueEntry) {
            if (piece.color === "white") {
              whiteValue += valueEntry.value;
            } else {
              blackValue += valueEntry.value;
            }
          }
        }
      } catch (error) {
        // Ignore errors - piece values are optional
      }
    }
  });

  return { whiteCount, blackCount, whiteValue, blackValue };
};

/**
 * Analyzes current pieces and determines top 4 pieces for promotion
 */
export const analyzeAndSetPromotionPieces = (
  currentPieces: Map<string, Piece>,
  boardRows: number
): string[] => {
  const pieceTypesSet = new Set<string>();

  currentPieces.forEach((piece) => {
    const pieceType = piece.type.toLowerCase();
    if (
      pieceType !== "pawn" &&
      pieceType !== "knave" &&
      pieceType !== "squire" &&
      pieceType !== "king" &&
      pieceType !== "emperor"
    ) {
      pieceTypesSet.add(pieceType);
    }
  });

  const pieceTypesWithValues: Array<{ type: string; value: number }> = [];
  const boardSizeStr = `${boardRows}×${boardRows}`;

  pieceTypesSet.forEach((pieceType) => {
    const definition = PIECE_DEFINITIONS[pieceType];
    if (definition) {
      const abbreviation = definition.abbreviation.toUpperCase();

      try {
        const pieceValuesModule = require("../data/pieceValues.ts");
        const pieceValues = pieceValuesModule.PIECE_VALUES[abbreviation];

        if (pieceValues) {
          const valueEntry = pieceValues.find(
            (v: any) => v.boardSize === boardSizeStr
          );
          if (valueEntry) {
            pieceTypesWithValues.push({
              type: pieceType,
              value: valueEntry.value,
            });
          }
        }
      } catch (error) {
        console.warn(`Could not get value for piece type: ${pieceType}`, error);
      }
    }
  });

  pieceTypesWithValues.sort((a, b) => b.value - a.value);
  const top4Pieces = pieceTypesWithValues.slice(0, 4).map((p) => p.type);

  const defaultPieces = ["queen", "castle", "bishop", "knight"];
  while (top4Pieces.length < 4) {
    for (const defaultPiece of defaultPieces) {
      if (!top4Pieces.includes(defaultPiece) && top4Pieces.length < 4) {
        top4Pieces.push(defaultPiece);
      }
    }
    break;
  }

  return top4Pieces;
};

/**
 * Determines if a move is a promotion move
 */
export const isPromotionMove = (
  piece: Piece,
  toRow: number,
  boardRows: number
): boolean => {
  const pieceType = piece.type.toLowerCase();

  // Determine which piece types can promote based on board size
  if (boardRows === 8 || boardRows === 9) {
    if (pieceType !== "pawn") return false;
  } else if (boardRows >= 10 && boardRows <= 12) {
    if (pieceType !== "knave") return false;
  } else if (boardRows >= 13) {
    if (pieceType !== "knave" && pieceType !== "squire") return false;
  } else {
    return false;
  }

  // Check if piece reached promotion rank
  if (piece.color === "white" && toRow === 0) {
    return true;
  }

  if (piece.color === "black" && toRow === boardRows - 1) {
    return true;
  }

  return false;
};

/**
 * Analyzes opponent's material to determine if sufficient for checkmate
 * Used for timeout draws when opponent lacks mating material
 */
export const analyzeOpponentMaterial = (
  opponentColor: "white" | "black",
  pieces: Map<string, Piece>,
  boardRows: number,
  boardCols: number
): string => {
  if (boardRows < 8 || boardCols < 8) {
    return "";
  }

  const opponentPieces: Piece[] = [];
  pieces.forEach((piece) => {
    if (piece.color === opponentColor) {
      opponentPieces.push(piece);
    }
  });

  let kings = 0,
    queens = 0,
    rooks = 0,
    bishops = 0,
    knights = 0,
    pawns = 0;
  const bishopColors = new Set<string>();

  opponentPieces.forEach((piece) => {
    const type = piece.type.toLowerCase();
    if (type === "king") kings++;
    else if (type === "queen") queens++;
    else if (type === "castle" || type === "rook") rooks++;
    else if (type === "bishop") {
      bishops++;
      const isLightSquare = (piece.position.row + piece.position.col) % 2 === 0;
      bishopColors.add(isLightSquare ? "light" : "dark");
    } else if (type === "knight") knights++;
    else if (type === "pawn") pawns++;
  });

  // Check for insufficient material combinations
  if (
    kings === 1 &&
    queens === 0 &&
    rooks === 0 &&
    bishops === 0 &&
    knights === 0 &&
    pawns === 0
  ) {
    return "K";
  }
  if (
    kings === 1 &&
    queens === 0 &&
    rooks === 0 &&
    bishops === 0 &&
    knights === 1 &&
    pawns === 0
  ) {
    return "K+N";
  }
  if (
    kings === 1 &&
    queens === 0 &&
    rooks === 0 &&
    bishops === 1 &&
    knights === 0 &&
    pawns === 0
  ) {
    return "K+B";
  }
  if (
    kings === 1 &&
    queens === 0 &&
    rooks === 0 &&
    bishops === 0 &&
    knights === 2 &&
    pawns === 0
  ) {
    return "K+N+N";
  }
  if (
    kings === 1 &&
    queens === 0 &&
    rooks === 0 &&
    bishops === 2 &&
    knights === 0 &&
    pawns === 0
  ) {
    if (bishopColors.size === 2) {
      return "K+B+B (opposite colors)";
    }
  }
  if (
    kings === 1 &&
    queens === 0 &&
    rooks === 0 &&
    bishops === 1 &&
    knights === 1 &&
    pawns === 0
  ) {
    return "K+B+N";
  }
  if (kings === 1 && queens >= 1) {
    return "K+Q";
  }
  if (kings === 1 && rooks >= 1) {
    return "K+R";
  }
  if (kings === 1 && pawns >= 1) {
    return "K+Pawn(s)";
  }
  if (queens >= 1 || rooks >= 1 || pawns >= 1) {
    if (boardRows >= 10) {
      return "Any other combination of three or more pieces";
    }
    return "Any combination with Q/R/Pawn";
  }

  if (boardRows >= 10 && opponentPieces.length >= 3) {
    return "Any other combination of three or more pieces";
  }

  return "Sufficient material";
};

/**
 * Creates a promoted piece with proper definition
 */
export const createPromotedPiece = (
  originalPiece: Piece,
  promotionPieceType: string,
  toPosition: Position
): Piece | null => {
  const pieceKey = promotionPieceType.toLowerCase();
  const pieceDefinition = PIECE_DEFINITIONS[pieceKey];

  if (!pieceDefinition) {
    console.error(`Piece definition not found for: ${promotionPieceType}`);
    return null;
  }

  return {
    type: pieceKey,
    color: originalPiece.color,
    position: toPosition,
    abbreviation: pieceDefinition.abbreviation,
    hasMoved: true,
    rules: pieceDefinition.rules,
  };
};

/**
 * Convert board to FEN string for the native Rust engine
 * Format: piece_positions side_to_move castling en_passant halfmove fullmove rows cols
 * Note: Rust engine expects 8 fields with rows and cols as separate fields
 * @param moveNumber - The current full move number (1-based). Used for phase detection.
 */
export const boardToFen = (board: Board, sideToMove: PieceColor, moveNumber: number = 1): string => {
  const rows = board.dimensions.rows;
  const cols = board.dimensions.cols;
  const ranks: string[] = [];

  // Build piece placement string
  for (let row = 0; row < rows; row++) {
    let rank = "";
    let emptyCount = 0;

    for (let col = 0; col < cols; col++) {
      const piece = board.squares[row][col];

      if (piece) {
        // Add empty square count if any
        if (emptyCount > 0) {
          rank += emptyCount.toString();
          emptyCount = 0;
        }

        // Get FEN character for piece
        const fenChar = PIECE_TYPE_TO_FEN[piece.type.toLowerCase()] || "?";
        // Uppercase for white, lowercase for black
        rank += piece.color === "white" ? fenChar.toUpperCase() : fenChar.toLowerCase();
      } else {
        emptyCount++;
      }
    }

    // Add trailing empty squares
    if (emptyCount > 0) {
      rank += emptyCount.toString();
    }

    ranks.push(rank);
  }

  const piecePlacement = ranks.join("/");
  const side = sideToMove === "white" ? "w" : "b";

  // TODO: Proper castling rights detection
  const castling = "KQkq";

  // TODO: En passant square
  const enPassant = "-";

  // Halfmove and fullmove clocks
  const halfmove = "0";
  const fullmove = String(moveNumber);

  // Always include rows and cols as separate fields (Rust engine requires 8 fields)
  return `${piecePlacement} ${side} ${castling} ${enPassant} ${halfmove} ${fullmove} ${rows} ${cols}`;
};